import { isOutboundState } from './moduleStateSets.ts'
import { REFERENCE_WELL, TRAY_SLOTS, type MonitoringDeviceKey } from './monitoringModel.ts'

/**
 * # 🔴 임시다 — eric 지시 2026-09-21. 서버가 이 값을 줄 때까지만 산다.
 *
 * eric 원문: 「일단 두 케이스 둘 다, 막히면 목 데이터 임시로 추가로 넣고 계속 진행해 …
 * 하드코딩도 괜찮고, 만약에 데이터가 비어 있으면.」
 *
 * ## 무엇을 채우나
 *
 * 진행 프레임(`batch_progress`)이 **「어디로」를 말하지 않는다.** payload 키가 **정확히 8개**이고
 * (`ts`·`type`·`version`·`execution_id`·`step`·`total`·`message`·`result`) 실물 20,855 프레임
 * 전수에서 `target`·`source`·`payload_kind`·`position`·`well` 이 **한 건도 없었다**(2026-09-21).
 *
 * ⇒ 그 빈 칸을 여기서 채운다. 그림은 이 값을 받아 「트레이 → 로터 → 완료 트레이」를 그린다.
 *
 * ## 🔴 무엇이 오면 이 파일을 걷나
 *
 * **BR-45** — 진행 프레임에 스텝 인자(`source`·`target`·`payload_kind`)를 실어 달라는 요청.
 * 「없는 축을 만들어 달라」가 아니다: 모듈은 그 인자를 **이미 갖고 있다**
 * (`module-core/app/generated/sequences/centrifuge.pick.schema.json` 이
 * `required: [payload_kind, source]` 를 요구한다) — `module-core/app/core/publisher.py:360-368`
 * 이 발행에서 **빼고 있을 뿐**이다.
 *
 * ⇒ 그것이 실리는 날 **이 파일을 지우고** `stepActivityModel.ts` 가 `temporaryStepArgs(…)` 대신
 *   프레임의 인자를 읽게 **한 줄 바꾸면 끝난다.** 그림도 `StepArgs` 타입도 안 바뀐다 —
 *   🔴 **여기 나오는 모양이 곧 서버가 실어 보낼 모양**이기 때문이다. 새 축을 발명하지 않았다.
 *
 * ## 규칙 — 어겼다가 이틀을 썼다
 *
 * 1. **그림 안에 값을 박지 않는다.** 임시인 것은 전부 이 파일 하나에 있다. 그림은
 *    「이 구성요소에 이게 있다」만 받고 **그게 임시인지 모른다**
 * 2. **실물이 시계다.** 스텝 번호·동작 이름·모듈 상태는 **전부 서버 값**이고, 이 파일은
 *    그 위에 「무엇이 어디로」만 얹는다. 실물이 멈추면 목도 멈추고, 배치가 없으면 아무것도 없다
 *    (호출부 `stepActivity()` 가 그 넷을 먼저 거른다) — **목이 제멋대로 재생되지 않는다**
 * 3. **자리 이름을 새로 짓지 않는다.** `location_kind` 어휘는 모듈 시퀀스 스키마에서,
 *    칸 이름은 `TRAY_SLOTS` 에서 온다
 * 4. **끌 수 있다.** `temporaryStepArgs` 에 `enabled: false` 를 주면 서버 값만 남고,
 *    그때도 그림이 죽지 않아야 한다(시험이 지킨다)
 */

/**
 * 모듈 **내부** 자리 한 곳. 🔴 모양이 `sequences/*.schema.json` 의 `source`·`target` 과 같다.
 */
export interface StepLocation {
  /**
   * 자리 종류. ⚠ **정적 enum 이 아니다**(`DEC-12` — 권위는 모듈 런타임 선언).
   * 관측 어휘: `inlet_tray`·`outlet_tray`·`rotor_slot`·`counterweight_station`·
   * `front_zone`·`waste_container`·`equipment_stage`·`tc_rack`·`reagent_bay`·`tip_bay`·`shaker`
   */
  readonly location_kind: string
  /** 슬롯 번호 — **랩웨어 `well_names` 의 1-기반 색인**이다(`slot=1` ⇔ `well_names[0]`) */
  readonly slot?: number | undefined
}

/**
 * 스텝 한 건의 인자. 🔴 **모듈 시퀀스 스키마와 같은 키 이름**을 쓴다 —
 * 서버가 실어 보내기 시작하면 그대로 갈아끼울 수 있게.
 *
 * 두 계열이 있다(스키마가 실제로 그렇게 갈라져 있다):
 * - **집기/놓기 계열**(원심·어셈블러의 `pick`·`place`) — `payload_kind` + `source`/`target`
 * - **액체 계열**(염색기의 `aspirate`·`dispense_reagent`·`shake`) — `target_wells` + `reagent_*`
 */
export interface StepArgs {
  /** 대상물 종류(`hypicc_chamber`·`thickness_controller`). `DEC-12` — 정적 enum 없음 */
  readonly payload_kind?: string | undefined
  readonly source?: StepLocation | undefined
  readonly target?: StepLocation | undefined
  /** 염색기 계열 — 대상 웰 이름 배열 */
  readonly target_wells?: readonly string[] | undefined
  /** 염색기 `dispense_reagent` — 시약 웰 */
  readonly reagent_well?: string | undefined
  /** 염색기 `dispense_reagent` — 시약 랩웨어 ID */
  readonly reagent_labware_id?: string | undefined
  /** 염색기 `mount_tip` — 팁 종류 색인 */
  readonly tip_type_index?: number | undefined
}

/** 하이픽 챔버 — `sequences/*.schema.json` 예시의 `payload_kind` 값 그대로 */
const CHAMBER = 'hypicc_chamber'
/** Thickness Controller — 어셈블러가 챔버에 얹는 것(`EQ-ROB-02`) */
const TC = 'thickness_controller'

/** 대조 칸(`C3`)을 뺀 챔버 칸들. 로터·트레이를 오가는 것은 이쪽이다 */
const CHAMBER_SLOTS: readonly string[] = TRAY_SLOTS.filter((slot) => slot !== REFERENCE_WELL)

/** 로터 슬롯 수. 마주보는 쌍 2벌(1↔3 · 2↔4) */
const ROTOR_SLOTS = 4

/**
 * 챔버 하나를 옮기는 데 드는 스텝 수. 실물 기록에서 원심 한 회차가
 * `move_to → pick → move_to → place` 로 도는 것을 보고 잡았다(`frames2.jsonl`).
 * ⚠ **정확할 필요가 없다** — 스텝이 늘면 다음 칸으로 넘어가는 «속도»만 정한다.
 */
const STEPS_PER_CHAMBER = 4

/**
 * 그 스텝에서 **무엇이 어디로 가는가.**
 *
 * 🔴 **서버 값이 시계다.** `step`(스텝 번호)과 `state`(모듈 상태) 둘 다 서버가 준 것이고,
 *    이 함수는 그 둘을 읽어 「몇 번째 칸이 어느 자리로」를 만든다. 자기 타이머를 돌리지 않는다.
 *
 * @param enabled 끄면 `undefined` — 서버가 이 축을 주기 시작하면 이 인자가 필요 없어진다
 */
export function temporaryStepArgs(
  deviceKey: MonitoringDeviceKey,
  action: string | undefined,
  step: number | undefined,
  state: string | undefined,
  enabled = true,
): StepArgs | undefined {
  if (!enabled) return undefined
  if (action === undefined || step === undefined) return undefined
  if (deviceKey === 'cent') return centArgs(action, step, state)
  if (deviceKey === 'asm') return asmArgs(action, step, state)
  if (deviceKey === 'stain') return stainArgs(action, step)
  return undefined
}

/** 그 스텝이 다루고 있는 챔버 칸 이름(`A1`·`A2` …). 스텝이 늘면 다음 칸으로 넘어간다 */
function chamberAt(step: number): { name: string; index: number } {
  const order = Math.floor(step / STEPS_PER_CHAMBER) % CHAMBER_SLOTS.length
  return { name: CHAMBER_SLOTS[order] ?? '', index: order + 1 }
}

/** 그 칸이 로터에서 차지하는 자리. 네 자리를 돌아가며 쓴다 */
function rotorSlotFor(index: number): number {
  return ((index - 1) % ROTOR_SLOTS) + 1
}

/**
 * 원심분리기 — 「입구 트레이 → 로터 → 출구 트레이」 한 방향 흐름.
 * 🔴 **방향을 정하는 것은 서버가 준 모듈 상태다**(`LOADING` 이면 싣는 중 · `UNLOADING` 이면 꺼내는 중).
 *    그래서 목이 흐름을 «지어내지» 않고 서버가 말한 국면을 따라간다.
 */
function centArgs(action: string, step: number, state: string | undefined): StepArgs | undefined {
  const { index } = chamberAt(step)
  const rotor: StepLocation = { location_kind: 'rotor_slot', slot: rotorSlotFor(index) }
  const inlet: StepLocation = { location_kind: 'inlet_tray', slot: index }
  const outlet: StepLocation = { location_kind: 'outlet_tray', slot: index }
  /*
   * 🔴 **수거 대기도 꺼내는 국면이다**(정정 2026-09-22). 이 자리가 `UNLOADING` 만 보고 있어서,
   *    C 트레이가 전면으로 나와 기다리는 동안 목이 **「입구 트레이에서 집는다」** 고 말했다 — 방향이 거꾸로다.
   *    아래 `temporaryChamberFlow` 와 **같은 판정(`isOutboundState`)** 을 써야 한 화면이 한 말을 한다.
   * 🔴 이름을 여기서 비교하지 않는다 — 옛·새 이름(`WAITING_FOR_UNLOAD` → `WAITING_FOR_TRAY_UNLOAD` ·
   *    `WAITING_FOR_MANUAL_TRAY_UNLOAD` · `TRAY_UNLOADING`)은 `moduleStateSets.ts` 가 든다.
   * 근거: `06 §4.2b` 원심 `UNLOADING → WAITING_FOR_TRAY_UNLOAD → WAITING_FOR_MANUAL_TRAY_UNLOAD`.
   */
  const unloading = isOutboundState(state)

  if (action === 'pick') return { payload_kind: CHAMBER, source: unloading ? rotor : inlet }
  if (action === 'place') return { payload_kind: CHAMBER, target: unloading ? outlet : rotor }
  if (action === 'move_to') return { payload_kind: CHAMBER, target: unloading ? outlet : rotor }
  if (action === 'run_centrifugation') return { target: { location_kind: 'rotor_slot' } }
  if (action === 'wait_for_manual_load') return { target: { location_kind: 'inlet_tray' } }
  if (action === 'slide_tray')
    return { target: { location_kind: unloading ? 'outlet_tray' : 'inlet_tray' } }
  return undefined
}

/**
 * 어셈블러 — 챔버는 트레이에서 스테이지로 가고, Thickness Controller 가 랙에서 얹힌다.
 * UV 노출·반전 배출은 스테이지 위에서 일어난다(`06 §4.2b` — `expose_uv`→`UV` · `invert_drain`→`ROTATE_DRAIN`).
 */
function asmArgs(action: string, step: number, state: string | undefined): StepArgs | undefined {
  const { index } = chamberAt(step)
  const stage: StepLocation = { location_kind: 'equipment_stage', slot: 1 }
  const tcRack: StepLocation = { location_kind: 'tc_rack', slot: index }
  const inlet: StepLocation = { location_kind: 'inlet_tray', slot: index }
  const outlet: StepLocation = { location_kind: 'outlet_tray', slot: index }
  const tcPhase = state === 'TC_LOAD' || state === 'TC_REMOVE'
  /*
   * 🔴 **어셈블러 반출 국면 = `UNLOADING`(챔버 회수) · 수거 대기 · `TRAY_UNLOADING` · 은퇴 `OUTPUT`.**
   *    2026-09-26 `SM-MOD-02` 재정리로 어셈블러가 `LOADING`(챔버 장착)·`UNLOADING`(챔버 회수)을 갖게 됐고
   *    `OUTPUT` 은 은퇴했다(대응 없음 · 옛 기록용으로만 남는다). ⚠ 2026-09-22 에 「어셈블러에 `UNLOADING` 은
   *    없다」고 적었던 자리다 — 그때는 참이었다. 판정은 `isOutboundState` 한 곳이다.
   */
  const output = isOutboundState(state)

  if (action === 'pick') {
    if (tcPhase) return { payload_kind: TC, source: state === 'TC_REMOVE' ? stage : tcRack }
    return { payload_kind: CHAMBER, source: output ? stage : inlet }
  }
  if (action === 'place') {
    if (tcPhase) return { payload_kind: TC, target: state === 'TC_REMOVE' ? tcRack : stage }
    return { payload_kind: CHAMBER, target: output ? outlet : stage }
  }
  if (action === 'move_to') return { payload_kind: CHAMBER, target: output ? outlet : stage }
  if (action === 'expose_uv') return { target: stage }
  if (action === 'invert_drain') return { target: stage }
  if (action === 'rotate') return { target: stage }
  if (action === 'slide_tray')
    return { target: { location_kind: output ? 'outlet_tray' : 'inlet_tray' } }
  return undefined
}

/**
 * 염색기 — 피펫이 시약 베이에서 빨아 트레이 웰에 놓는다.
 * ⚠ **키 이름이 원심·어셈블러와 다르다**(`target_wells`·`reagent_well`). 스키마가 그렇게 갈라 두었고
 *   여기서 통일하지 않는다 — 통일하면 서버 값으로 갈아끼울 때 안 맞는다.
 */
function stainArgs(action: string, step: number): StepArgs | undefined {
  const { name, index } = chamberAt(step)
  const wells = [name]
  /** 시약 웰 — `dispense_reagent` 예시의 `RA1` 꼴. 베이에 여러 칸이 있다 */
  const reagentWell = `RA${String((index % 4) + 1)}`

  if (action === 'aspirate') {
    return { target_wells: wells, source: { location_kind: 'reagent_bay', slot: index } }
  }
  if (action === 'dispense_reagent') {
    return {
      target_wells: wells,
      reagent_well: reagentWell,
      reagent_labware_id: 'staining.reagent_bay.ambient',
      target: { location_kind: 'shaker', slot: index },
    }
  }
  if (action === 'mount_tip') {
    return { tip_type_index: (index % 8) + 1, source: { location_kind: 'tip_bay', slot: index } }
  }
  if (action === 'discard_tip') return { target: { location_kind: 'waste_container' } }
  if (action === 'shake') return { target_wells: wells, target: { location_kind: 'shaker' } }
  if (action === 'wait_with_temp_monitoring') {
    return { target_wells: wells, target: { location_kind: 'shaker' } }
  }
  if (action === 'move_to') return { target: { location_kind: 'shaker', slot: index } }
  if (action === 'slide_tray') return { target: { location_kind: 'inlet_tray' } }
  return undefined
}

/**
 * 지금 챔버 칸들이 **어디 있는가** — 「입구 트레이 · 장비 안 · 완료 트레이」 세 자리.
 *
 * 🔴 **칸 이름만 옮긴다. 시료 ID 를 붙이지 않는다.** 어느 시료가 어느 칸인지는 서버 어디에도
 *    없고(BR-31) eric 본인이 **트레이 단위로 움직인다**고 정했다. 그래서 그림이 말하는 것은
 *    **「`A1` 칸이 로터에 있다」**까지이고 **「`S-1045` 가 로터에 있다」가 아니다** —
 *    후자를 그렸다가 걷어낸 자리다. 되살리지 마라.
 *
 * 🔴 실물 `step` 과 서버가 준 `state` 만으로 만든다. 자기 타이머가 없다.
 */
export interface ChamberFlow {
  /** 아직 입구 트레이에 있는 칸 */
  readonly inlet: readonly string[]
  /** 지금 장비 안(로터 · 스테이지 · 셰이커)에 있는 칸 */
  readonly inModule: readonly string[]
  /** 작업이 끝나 완료 트레이로 나간 칸 */
  readonly outlet: readonly string[]
}

const NO_FLOW: ChamberFlow = { inlet: CHAMBER_SLOTS, inModule: [], outlet: [] }

/**
 * 스텝이 나아가면 칸이 **입구 → 장비 안 → 완료**로 옮겨 간다.
 *
 * 원심은 로터가 4자리라 **회차**로 움직인다(4칸 싣고 → 돌리고 → 4칸 꺼낸다).
 * 어셈블러·염색기는 한 칸씩 지난다.
 */
export function temporaryChamberFlow(
  deviceKey: MonitoringDeviceKey,
  step: number | undefined,
  state: string | undefined,
  enabled = true,
): ChamberFlow {
  if (!enabled || step === undefined || deviceKey === 'robot') return NO_FLOW

  const order = Math.floor(step / STEPS_PER_CHAMBER) % CHAMBER_SLOTS.length

  if (deviceKey === 'cent') {
    const round = Math.floor(order / ROTOR_SLOTS)
    const within = order % ROTOR_SLOTS
    const roundStart = round * ROTOR_SLOTS
    const roundEnd = roundStart + ROTOR_SLOTS
    // 🔴 꺼내는 국면이면 장비 안이 줄고 완료 쪽이 는다 — **방향을 서버 상태가 정한다**(`isOutboundState`)
    const unloading = isOutboundState(state)
    const loadedEnd = unloading ? roundEnd : roundStart + within + 1
    const outEnd = unloading ? roundStart + within + 1 : roundStart
    return {
      outlet: CHAMBER_SLOTS.slice(0, outEnd),
      inModule: CHAMBER_SLOTS.slice(unloading ? outEnd : roundStart, loadedEnd),
      inlet: CHAMBER_SLOTS.slice(roundEnd),
    }
  }

  // 어셈블러·염색기 — 한 칸씩 장비를 지난다
  return {
    outlet: CHAMBER_SLOTS.slice(0, order),
    inModule: CHAMBER_SLOTS.slice(order, order + 1),
    inlet: CHAMBER_SLOTS.slice(order + 1),
  }
}

/**
 * 🔴 **임시로 채우고 있는 값의 수.** 시험이 이 수를 센다 — 목을 걷을 때 **0 이 되어야 한다.**
 * 늘리거나 줄이면 시험이 먼저 말해 준다(조용히 늘어나는 것을 막는 자리).
 *
 * | # | 무슨 값 | 왜 프레임이 못 주나 | 어느 BR |
 * | --- | --- | --- | --- |
 * | 1 | `payload_kind` | 프레임 payload 8키에 없다 | BR-45 |
 * | 2 | `source.location_kind` · `slot` | 〃 | BR-45 |
 * | 3 | `target.location_kind` · `slot` | 〃 | BR-45 |
 * | 4 | `target_wells`(염색기) | 〃 | BR-45 |
 * | 5 | `reagent_well` · `reagent_labware_id` | 〃 | BR-45 |
 * | 6 | `tip_type_index` | 〃 | BR-45 |
 * | 7 | 칸↔스텝 대응(`chamberAt`) | 컴파일된 스텝 목록을 L2 가 안 준다(경로 68개에 0건) | BR-47 |
 * | 8 | 칸의 소재(`temporaryChamberFlow`) | 트레이·챔버의 현재 자리를 L2 가 안 준다 | BR-48 |
 */
export const TEMPORARY_MOCK_FIELDS: readonly string[] = [
  'payload_kind',
  'source',
  'target',
  'target_wells',
  'reagent_well',
  'reagent_labware_id',
  'tip_type_index',
  'chamber_flow',
]
